import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthenticatedUser, Owner, Reminder} from '../../models';
import {ReminderStatusTranslationsService} from 'src/app/shared/translations';
import {StorageService} from '../storage.service';
import {environment} from '~environments/environment';
import {UserReminder} from '../../models/customer/user-reminder';

/**
 * Service for creating and updating reminder entity.
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ReminderService {
  public authenticatedUser: AuthenticatedUser;
  public API_URL: any = environment.envAPI;
  public API_MEDIA_OBJECTS: any = `${environment.envAPI}/attachments`;

  public totalOpenReminders: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public castTotalOpenReminders: any = this.totalOpenReminders.asObservable();

  public reminderList: BehaviorSubject<Array<Reminder | UserReminder>> = new BehaviorSubject<Array<Reminder>>(null);
  public castReminderList: any = this.reminderList.asObservable();

  public currentReminder: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castCurrentReminder: any = this.currentReminder.asObservable();

  public currentPossibleOwnerList: BehaviorSubject<Array<Owner>> = new BehaviorSubject<Array<Owner>>(null);
  public castCurrentPossibleOwnerList: any = this.currentPossibleOwnerList.asObservable();

  constructor(
    private http: HttpClient,
    private translations: ReminderStatusTranslationsService,
    private storage: StorageService
  ) {
    this.storage.authenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => {
        this.authenticatedUser = user;
      });
  }

  /**
   * Get all reminders for a customer.
   */
  public getAll(customerId: number, onlyOpenReminders: boolean = false): Observable<Array<Reminder>> {
    let params = new HttpParams()
      .set('order[deadline]', 'asc');

    if (onlyOpenReminders) {
      params = params
        .set('status', 'open')
        .set('page', '1')
        .set('itemsPerPage', '5');
    }

    return this.http.get<Array<Reminder>>(`${this.API_URL}/customers/${customerId}/reminders`, {params}).pipe(
      map(res => {
        if (onlyOpenReminders) {
          this.setTotalOpenReminders(res['hydra:totalItems']);
        }

        return res['hydra:member'].map((reminderData: Reminder) => new Reminder(reminderData, this.translations));
      }),
    );
  }

  /**
   * Get all reminders for a customer.
   */
  public getAllCustomerRemindersEndpoint(customerId: number): any {
    const api = `${this.API_URL}/customers/${customerId}/reminders`;

    const params: Array<any> = [
      {'order[status]': 'desc'},
      {'order[deadline]': 'asc'},
      {club: `api/clubs/${this.storage.selectedClubValue.id}`},
    ];

    return [api, params];
  }

  /**
   * Get all reminders for a customer with a specific status.
   */
  public getStatusUserRemindersEndpoint(userId: number, status: string = 'open'): any {
    const api = `${this.API_URL}/clubs/${this.storage.selectedClubValue.id}/users/${userId}/reminders`;

    const params: Array<any> = [
      {status},
      {'order[deadline]': 'asc'},
    ];

    return [api, params];
  }

  /**
   * Create the reminder class object.
   */
  public createRemindersClass(reminders: Array<Reminder>): Array<Reminder> {
    return reminders.map((reminder: Reminder) => new Reminder(reminder, this.translations));
  }

  /**
   * Create the user reminder class object.
   */
  public createUserRemindersClass(reminders: Array<UserReminder>): Array<UserReminder> {
    return reminders.map((reminder: UserReminder) => new UserReminder(reminder, this.translations));
  }

  /**
   * Create a new reminder.
   */
  public create(reminder: Reminder): Observable<Reminder> {
    return this.http.post<Reminder>(`${this.API_URL}/customer-reminders`, reminder).pipe(
      map(res => new Reminder(res, this.translations))
    );
  }

  /**
   * Update reminder.
   */
  public update(reminder: Reminder): Observable<Reminder> {
    return this.http.patch<Reminder | any>(`${this.API_URL}/customer-reminders/${reminder.id}`, reminder).pipe(
      map(res => new Reminder(res, this.translations))
    );
  }

  /**
   * Get possible reminder owners.
   */
  public getPossibleReminderOwners(cludId: number): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/me/clubs/${cludId}/possible-reminder-owners`).pipe(
      map(res => res.map((owners: Owner) => new Owner(owners)))
    );
  }

  /**
   * Delete reminder.
   */
  public delete(reminderId: number): any {
    return this.http.delete(`${this.API_URL}/customer-reminders/${reminderId}`);
  }

  /**
   * Upload an image.
   */
  public uploadAttachment(attachment: FormData): Observable<any> {
    return this.http.post<any>(`${this.API_MEDIA_OBJECTS}`, attachment);
  }

  /**
   * Retrieve attachment path / name.
   */
  public getPathToImageByMediaObject(mediaObjectIri: string): Observable<any> {
    const thirdItem: number = 3;
    const mediaObjectId = mediaObjectIri.split('/')[thirdItem];
    return this.http.get<any>(`${this.API_MEDIA_OBJECTS}/${mediaObjectId}`);
  }

  /**
   * Adds values to the observer.
   */
  public announceCurrentReminder(reminder: Reminder): void {
    this.currentReminder.next(reminder);
  }

  /**
   * Clear the address values from the observable.
   */
  public clearCurrentReminder(): void {
    this.currentReminder.next(null);
  }

  /**
   * Set the total open reminders
   */
  public setTotalOpenReminders(numberOfReminders: number): void {
    this.totalOpenReminders.next(numberOfReminders);
  }

  /**
   * Set a new reminder list
   */
  public setReminderList(reminderList: Array<Reminder | UserReminder>): void {
    this.reminderList.next(reminderList);
  }

  /**
   * Set a new possible owner list
   */
  public setPossibleOwnerList(ownerList: Array<any>): void {
    this.currentPossibleOwnerList.next(ownerList);
  }

  /**
   * Delete a reminder element from the reminder list using the id
   */
  public deleteReminderFromList(reminderId: number): void {
    const index = this.reminderList.getValue().findIndex(el => el.id === reminderId);
    this.reminderList.getValue().splice(index, 1);
  }

  /**
   * Add a new entry to the reminder list
   */
  public addToReminderList(reminder: Reminder): void {
    this.reminderList.getValue().push(reminder);
  }

  /**
   * Update a certain entry in the reminder list
   */
  public updateReminder(reminder: Reminder): void {
    const index = this.reminderList.getValue().findIndex(el => el.id === reminder.id);
    this.reminderList.getValue()[index] = reminder;
  }
}
